/**
 * §A.3 · La salud de los clubes.
 *
 * ## «El trabajo diario de Manuel, servido»
 *
 * Es la frase del contrato, y detrás hay un hecho: **un club que deja de pasar lista
 * se da de baja en unos sesenta días**. Esta lista avisa antes de que lo diga.
 *
 * ## Ordenar por riesgo a secas es engañoso
 *
 * Lo dice el DTO: «un club en `critical` de ocho alumnos y uno en `watch` de
 * trescientos no piden la misma llamada esta tarde». Por eso el MRR y los alumnos van
 * en la fila, al lado del nivel, y no escondidos en un detalle.
 *
 * ## Aquí no se calcula nada
 *
 * El score y el nivel los mide el servidor. Recalcular el riesgo en el cliente es
 * cómo una pantalla de vigilancia acaba mintiendo, y además `components` trae el peso
 * de cada pieza justamente **para poder discutir el número** sin rehacerlo.
 *
 * ## Recalcular a mano existe porque el proceso corre de noche
 *
 * Después de arreglarle algo a un club, la pregunta es «¿ha subido?», y esperar a
 * mañana no sirve. Pisa la medición del día, así que se dice antes de pulsar.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha, numero } from '@/shared/ui/formato';
import {
  etiquetaDeComponente,
  RIESGOS,
  type SaludConDetalle,
  type SaludDeClub,
} from '@/backoffice/tipos';

const RIESGOS_FILTRO: readonly { valor: string; etiqueta: string }[] = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'critical', etiqueta: 'Solo críticos' },
  { valor: 'at_risk', etiqueta: 'Solo en riesgo' },
  { valor: 'watch', etiqueta: 'Solo a vigilar' },
  { valor: 'ok', etiqueta: 'Solo los que van bien' },
];

/** Un porcentaje que puede no poder medirse. `null` no es cero. */
const porcentaje = (v: number | null): string =>
  v === null ? '—' : `${numero(v * 100, 0)} %`;

/**
 * Un valor cualquiera, legible.
 *
 * Dos cuidados: un `String(objeto)` da «[object Object]» —lo cazó el
 * `no-base-to-string` de ESLint, que aquí acertó— y un decimal con `String` sale con
 * el punto inglés. Los enteros se dejan tal cual: un contador con dos decimales se
 * lee peor.
 */
function comoTexto(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (typeof v === 'number') {
    return Number.isInteger(v) ? String(v) : numero(v, 2);
  }
  try {
    return JSON.stringify(v) ?? '—';
  } catch {
    // Una referencia circular en un diagnóstico no puede tirar la pantalla.
    return '—';
  }
}

/**
 * Un componente del score, leído sin inventarle una forma.
 *
 * `components` es un `Record<string, unknown>` y el contrato **no** promete una
 * estructura; lo que sí promete es el contenido: «cada componente con su peso y qué
 * mide, para poder discutir el número». Así que se leen `note`, `weight` y la tasa
 * —`rate` o `count`— **si están**, y si no se cae al JSON entero.
 *
 * La primera versión pintaba el JSON siempre, y era honesta y bastante inútil: la
 * explicación de cada pieza acababa entre llaves en una pantalla cuyo único propósito
 * es poder discutir el número. Leer campos opcionales con salida por defecto no es
 * inventarse un contrato; escribir `components.attendance.rate` sin comprobarlo sí.
 */
function leerComponente(v: unknown): {
  nota: string | null;
  peso: number | null;
  tasa: string | null;
  crudo: string | null;
} {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    return { nota: null, peso: null, tasa: null, crudo: comoTexto(v) };
  }

  const o = v as Record<string, unknown>;
  const nota = typeof o['note'] === 'string' ? o['note'] : null;
  const peso = typeof o['weight'] === 'number' ? o['weight'] : null;

  const tasa =
    typeof o['rate'] === 'number'
      ? porcentaje(o['rate'])
      : o['rate'] === null
        ? // `null` es «no se puede medir», y no cero.
          'no se puede medir'
        : typeof o['count'] === 'number'
          ? comoTexto(o['count'])
          : null;

  // Sin nada reconocible, el JSON entero: es un diagnóstico y algo hay que enseñar.
  if (nota === null && peso === null && tasa === null) {
    return { nota: null, peso: null, tasa: null, crudo: comoTexto(v) };
  }

  return { nota, peso, tasa, crudo: null };
}

export function SaludDeLosClubes({
  alAbrirClub,
}: {
  alAbrirClub: (clubId: string) => void;
}) {
  const cache = useQueryClient();
  const [riesgo, setRiesgo] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const salud = useQuery({
    queryKey: ['admin', 'salud', riesgo],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['health-scores'].get({
        query: {
          ...(riesgo === '' ? {} : { risk: riesgo as 'critical' }),
          limit: 200,
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SaludDeClub[];
    },
  });

  const detalle = useQuery({
    queryKey: ['admin', 'salud-detalle', abierto],
    enabled: abierto !== null,
    queryFn: async () => {
      const { data, error } = await api.v1.admin['health-scores']({
        academy: abierto!,
      }).get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SaludConDetalle;
    },
  });

  const recalcular = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin['health-scores'].recompute.post(
        {},
        /*
         * **`Idempotency-Key`, y se me había olvidado.** La ruta lleva
         * `idempotent: true` y el servidor responde `400
         * idempotency_key_missing` explicando exactamente qué falta —el mensaje es
         * bueno y por eso el fallo costó un minuto—.
         *
         * La lista fiable de las que la exigen no es la tabla de `endpoints.md`
         * sino un `grep 'idempotent: true'` en las rutas: son nueve en §A, y
         * recalcular la salud es una de ellas porque reescribe la medición del día
         * de los doscientos clubes.
         *
         * Clave nueva por pulsación: una guardada haría que el segundo intento
         * devolviera la respuesta del primero, y aquí lo que se quiere es
         * recalcular otra vez.
         */
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['admin', 'salud'] });
      void cache.invalidateQueries({ queryKey: ['admin', 'salud-detalle'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (salud.isPending)
    return <Cargando filas={6} etiqueta="Cargando la salud" />;

  if (salud.isError || !salud.data) {
    return (
      <PanelError
        problema={aProblema(salud.error)}
        reintentar={() => void salud.refetch()}
      />
    );
  }

  return (
    <section>
      <div className="titulo-fila">
        <h1>Salud de los clubes</h1>
        <button
          type="button"
          className="btn-plano"
          disabled={recalcular.isPending}
          onClick={() => {
            setProblema(null);
            recalcular.mutate();
          }}
        >
          {recalcular.isPending ? 'Recalculando…' : 'Recalcular ahora'}
        </button>
      </div>

      <p className="tenue">
        <strong>
          Un club que deja de pasar lista se da de baja en unos sesenta días.
        </strong>{' '}
        Esta lista avisa antes. La medición corre de noche; recalcular ahora
        pisa la del día y sirve para ver si algo que acabas de arreglar ha
        subido.
      </p>

      {problema && <PanelError problema={problema} />}

      <div className="filtros">
        <label>
          Riesgo
          <select value={riesgo} onChange={(e) => setRiesgo(e.target.value)}>
            {RIESGOS_FILTRO.map((r) => (
              <option key={r.valor} value={r.valor}>
                {r.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {salud.data.length === 0 ? (
        <Vacio
          titulo="Sin medir todavía"
          explicacion="La medición corre cada noche y necesita clubes con algo de actividad detrás. Con «Recalcular ahora» se fuerza una pasada."
        />
      ) : (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Club</th>
                <th className="num">Score</th>
                <th>Riesgo</th>
                <th className="num">Listas</th>
                <th className="num">App</th>
                <th className="num">Impago</th>
                <th className="num">Alumnos</th>
                <th className="num">En juego</th>
              </tr>
            </thead>
            <tbody>
              {salud.data.map((c) => {
                const r = RIESGOS[c.risk_level];
                return (
                  <tr key={c.academy_id}>
                    <td>
                      <button
                        type="button"
                        className="enlace-fila"
                        onClick={() =>
                          setAbierto(
                            abierto === c.academy_id ? null : c.academy_id
                          )
                        }
                      >
                        {c.trade_name}
                      </button>
                      <br />
                      <span className="tenue">
                        medido el {fecha(c.measured_on)}
                      </span>
                    </td>
                    <td className="num">
                      <strong>{c.score}</strong>
                    </td>
                    <td>
                      {r ? (
                        <span className={`pastilla ${r.clase}`}>
                          {r.etiqueta}
                        </span>
                      ) : (
                        c.risk_level
                      )}
                    </td>
                    {/*
                      Las tres tasas pueden ser `null` y entonces salen con raya: un
                      club sin clases en agosto no está en riesgo, está en agosto.
                    */}
                    <td className="num">{porcentaje(c.attendance_rate)}</td>
                    <td className="num">{porcentaje(c.app_adoption_rate)}</td>
                    <td className="num">{porcentaje(c.overdue_rate)}</td>
                    <td className="num">{c.students}</td>
                    {/* Lo que está en juego al mes: es lo que ordena la tarde. */}
                    <td className="num">{euros(c.mrr_cents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── El detalle, debajo y sin cambiar de pantalla ──────────────────── */}
      {abierto && (
        <>
          <h2 className="seccion">De dónde sale el número</h2>

          {detalle.isLoading && <Cargando filas={3} etiqueta="Cargando" />}

          {detalle.isError && (
            <PanelError
              problema={aProblema(detalle.error)}
              reintentar={() => void detalle.refetch()}
            />
          )}

          {detalle.data && (
            <>
              <div className="titulo-fila">
                <h3>{detalle.data.trade_name}</h3>
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => alAbrirClub(detalle.data!.academy_id)}
                >
                  Abrir la ficha del club
                </button>
              </div>

              {/*
                La historia va primero que los componentes, y es lo que dice el
                contrato: «un 62 no dice nada; un 62 que era 81 hace tres semanas
                es una llamada esta tarde».
              */}
              {detalle.data.history.length > 1 ? (
                <div className="tabla-marco">
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th>Medido</th>
                        <th className="num">Score</th>
                        <th>Riesgo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.data.history.map((h) => {
                        const r = RIESGOS[h.risk_level];
                        return (
                          <tr key={String(h.measured_on)}>
                            <td>{fecha(h.measured_on)}</td>
                            <td className="num">{h.score}</td>
                            <td>
                              {r ? (
                                <span className={`pastilla ${r.clase}`}>
                                  {r.etiqueta}
                                </span>
                              ) : (
                                h.risk_level
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="tenue">
                  Solo hay una medición, así que no se puede ver si sube o baja.
                  Y eso es la mitad de la información: hacen falta unas semanas.
                </p>
              )}

              <h3>Los componentes, con su peso</h3>

              <p className="tenue">
                Están aquí para poder discutir el número sin rehacerlo. La tasa
                de listas pesa el 40 %: es la única de las cuatro que ha
                demostrado predecir la baja.
              </p>

              <ul className="papeles">
                {Object.entries(detalle.data.components).map(([k, v]) => {
                  const c = leerComponente(v);
                  return (
                    <li key={k}>
                      <div className="aviso-cabeza">
                        <strong>{etiquetaDeComponente(k)}</strong>
                        {c.peso !== null && (
                          <span className="pastilla pastilla-left">
                            pesa el {c.peso} %
                          </span>
                        )}
                        {c.tasa !== null && (
                          <span className="pastilla pastilla-left">
                            {c.tasa}
                          </span>
                        )}
                      </div>
                      {c.nota && <p>{c.nota}</p>}
                      {/* Lo que no se reconoce se enseña tal cual: es un diagnóstico. */}
                      {c.crudo && (
                        <p className="tenue">
                          <code>{c.crudo}</code>
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
